import { useId, useRef } from "react";
import { useHeroIllustrationAnimation } from "@/hooks/useHeroIllustrationAnimation";

/**
 * Subiza hero illustration (3D / oblique view): a teacher, seen from behind,
 * watches students take a quiz. Student screens face the students, so each PC
 * shows its back and a floating popup reports that student's progress.
 *
 * Colors come from Tailwind theme tokens only (primary, primary-glow, foreground,
 * card). The exception is skin tones: fixed hex values so characters stay
 * diverse in every theme.
 *
 * GSAP hooks: .teacher  .student  .popup  .progress-ring  .screen  .led
 *             .float-card  .badge
 */

type HairStyle = "short" | "curly" | "long" | "bun";

type Student = {
  x: number;
  skin: string;
  hair: string; // full literal Tailwind classes so the scanner can find them
  style: HairStyle;
  shirt: string;
  progress: number; // 0-1
  ring: string;
  answers: [number, number, number, number]; // 0 answered, 1 flagged, 2 todo
};

const STUDENTS: Student[] = [
  {
    x: 100,
    skin: "#F1C9A5",
    hair: "fill-foreground",
    style: "short",
    shirt: "fill-accent",
    progress: 0.42,
    ring: "stroke-accent",
    answers: [0, 0, 1, 2],
  },
  {
    x: 250,
    skin: "#8D5A3B",
    hair: "fill-foreground",
    style: "curly",
    shirt: "fill-accent-glow",
    progress: 0.25,
    ring: "stroke-accent",
    answers: [0, 2, 2, 2],
  },
  {
    x: 400,
    skin: "#D9A47A",
    hair: "fill-foreground",
    style: "long",
    shirt: "fill-accent/60",
    progress: 1,
    ring: "stroke-accent-glow",
    answers: [0, 0, 0, 0],
  },
  {
    x: 550,
    skin: "#5A3522",
    hair: "fill-foreground",
    style: "bun",
    shirt: "fill-foreground/70",
    progress: 0.6,
    ring: "stroke-accent",
    answers: [0, 0, 0, 2],
  },
  {
    x: 700,
    skin: "#F1C9A5",
    hair: "fill-accent-glow",
    style: "short",
    shirt: "fill-accent",
    progress: 0.75,
    ring: "stroke-accent",
    answers: [0, 0, 1, 0],
  },
];

const ANSWER_DOT = ["fill-accent", "fill-accent-glow", "fill-foreground/15"] as const;

const RING_C = 2 * Math.PI * 11;
const STAR =
  "M0 -9 L2.12 -2.91 L8.56 -2.78 L3.42 1.11 L5.29 7.28 L0 3.6 L-5.29 7.28 L-3.42 1.11 L-8.56 -2.78 L-2.12 -2.91 Z";
const SHORT_HAIR = "M-16 165 Q-17 148 0 148 Q17 148 16 165 Q10 156 0 157 Q-10 156 -16 165Z";
const ARM = "M384 508 Q430 490 486 452 L498 470 Q450 508 396 538 Z";

// perspective floor: lines fan out from a horizon at y=300
const FLOOR_FAN = Array.from({ length: 9 }, (_, i) => -400 + i * 200);
const FLOOR_ROWS = [316, 338, 370, 416, 484, 584];

function HairBack({ style, className }: { style: HairStyle; className: string }) {
  if (style !== "long") return null;
  return <rect x="-19" y="147" width="38" height="36" rx="17" className={className} />;
}

function HairFront({ style, className }: { style: HairStyle; className: string }) {
  if (style === "curly") {
    return (
      <g className={className}>
        <circle cx="-11" cy="154" r="8" />
        <circle cx="0" cy="150" r="9" />
        <circle cx="11" cy="154" r="8" />
        <circle cx="-15" cy="163" r="5" />
        <circle cx="15" cy="163" r="5" />
      </g>
    );
  }
  return (
    <g className={className}>
      <path d={SHORT_HAIR} />
      {style === "bun" && <circle cx="0" cy="143" r="7" />}
    </g>
  );
}

/** Floating progress card hovering above a student's PC. */
function ProgressPopup({ s }: { s: Student }) {
  const done = s.progress >= 1;
  return (
    <g transform="translate(0 -150)">
      <g className="popup">
        <rect x="-46" y="-41" width="92" height="46" rx="11" className="fill-foreground/10" />
        <rect x="-46" y="-46" width="92" height="46" rx="11" className="fill-card" />
        <path d="M-7 0 L0 9 L7 0 Z" className="fill-card" />

        <circle
          cx="-28"
          cy="-23"
          r="11"
          fill="none"
          strokeWidth="4.5"
          className="stroke-foreground/10"
        />
        <circle
          cx="-28"
          cy="-23"
          r="11"
          fill="none"
          strokeWidth="4.5"
          strokeLinecap="round"
          strokeDasharray={RING_C}
          strokeDashoffset={RING_C * (1 - s.progress)}
          data-offset={RING_C * (1 - s.progress)}
          data-circumference={RING_C}
          transform="rotate(-90 -28 -23)"
          className={`progress-ring ${s.ring}`}
        />
        {done && (
          <path
            d="M-33 -23 L-29 -19 L-22 -27"
            fill="none"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="stroke-accent-glow"
          />
        )}

        <rect x="-10" y="-35" width="48" height="5" rx="2.5" className="fill-foreground/10" />
        <rect x="-10" y="-35" width={48 * s.progress} height="5" rx="2.5" className="fill-accent" />
        <rect x="-10" y="-25" width="34" height="4" rx="2" className="fill-foreground/25" />
        {s.answers.map((a, i) => (
          <circle key={i} cx={-7 + i * 10} cy="-11" r="3" className={ANSWER_DOT[a]} />
        ))}
      </g>
    </g>
  );
}

function StudentFigure({ s, index }: { s: Student; index: number }) {
  return (
    <g className="student" data-student={index + 1} transform={`translate(${s.x} 290)`}>
      {/* floor shadow + legs */}
      <ellipse cx="6" cy="40" rx="84" ry="7" className="fill-foreground/12" />
      <rect x="-60" y="10" width="6" height="30" className="fill-foreground/30" />
      <rect x="54" y="10" width="6" height="30" className="fill-foreground/30" />

      {/* character, seated behind the monitor; shoulders stop at its top edge */}
      <g transform="translate(0 -268)">
        <HairBack style={s.style} className={s.hair} />
        <path d="M-27 202 Q-27 184 0 183 Q27 184 27 202 Z" className={s.shirt} />
        <rect x="-5" y="178" width="10" height="9" fill={s.skin} />
        <circle cx="0" cy="166" r="15" fill={s.skin} />
        <HairFront style={s.style} className={s.hair} />
        <circle cx="-5.5" cy="167" r="1.5" className="fill-foreground" />
        <circle cx="5.5" cy="167" r="1.5" className="fill-foreground" />
        <ellipse cx="0" cy="173.5" rx="2.5" ry="1" className="fill-foreground/50" />
      </g>

      {/* desk box: front, top, side */}
      <rect x="-66" y="0" width="132" height="10" className="fill-foreground/30" />
      <path d="M-66 0 L66 0 L80 -11 L-52 -11 Z" className="fill-foreground/12" />
      <path d="M66 0 L80 -11 L80 -1 L66 10 Z" className="fill-foreground/45" />

      {/* monitor, seen from behind: screen faces the student */}
      <g className="monitor">
        <path d="M-46 -58 L-36 -66 L56 -66 L46 -58 Z" className="fill-foreground/55" />
        <path d="M46 -58 L56 -66 L56 -14 L46 -6 Z" className="fill-foreground" />
        <rect x="-46" y="-58" width="92" height="52" rx="6" className="fill-foreground/85" />
        <circle cx="0" cy="-32" r="6" className="fill-card/35" />
        <circle cx="34" cy="-12" r="1.8" className="led fill-accent-glow" />
      </g>

      <ProgressPopup s={s} />
    </g>
  );
}

function Stars() {
  return (
    <>
      {[0, 1, 2, 3, 4].map((i) => (
        <path
          key={i}
          d={STAR}
          transform={`translate(${24 + i * 20} 24)`}
          className={i < 4 ? "fill-accent-glow" : "fill-foreground/15"}
        />
      ))}
    </>
  );
}

export function HeroIllustration({ className = "" }: { className?: string }) {
  const ref = useRef<SVGSVGElement>(null);
  const clipId = `hero-room-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  useHeroIllustrationAnimation(ref);

  return (
    <svg
      ref={ref}
      viewBox="0 0 800 680"
      role="img"
      aria-label="A teacher watching live quiz results while students take an exam; each student's computer shows a progress popup"
      focusable="false"
      preserveAspectRatio="xMidYMid meet"
      className={["h-auto w-full max-w-full", className].filter(Boolean).join(" ")}
    >
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y="90" width="800" height="590" rx="36" />
        </clipPath>
      </defs>

      {/* room: wall + perspective floor */}
      <g clipPath={`url(#${clipId})`}>
        <rect x="0" y="90" width="800" height="210" className="fill-accent/6" />
        <rect x="0" y="292" width="800" height="8" className="fill-foreground/8" />
        <rect x="0" y="300" width="800" height="380" className="fill-accent/12" />
        {FLOOR_FAN.map((xb) => (
          <line
            key={xb}
            x1={400 + (xb - 400) * 0.28}
            y1="300"
            x2={xb}
            y2="680"
            strokeWidth="1.5"
            className="stroke-accent/10"
          />
        ))}
        {FLOOR_ROWS.map((y) => (
          <line
            key={y}
            x1="0"
            y1={y}
            x2="800"
            y2={y}
            strokeWidth="1.5"
            className="stroke-accent/10"
          />
        ))}
      </g>

      {/* students */}
      <g className="students">
        {STUDENTS.map((s, i) => (
          <StudentFigure key={s.x} s={s} index={i} />
        ))}
      </g>

      {/* plant */}
      <g className="decor">
        <ellipse cx="752" cy="580" rx="30" ry="6" className="fill-foreground/12" />
        <ellipse
          cx="744"
          cy="540"
          rx="7"
          ry="20"
          transform="rotate(-22 744 540)"
          className="fill-accent-glow"
        />
        <ellipse
          cx="760"
          cy="538"
          rx="7"
          ry="22"
          transform="rotate(18 760 538)"
          className="fill-accent-glow/70"
        />
        <ellipse cx="752" cy="534" rx="6" ry="22" className="fill-accent-glow" />
        <rect x="737" y="560" width="30" height="22" rx="6" className="fill-accent" />
      </g>

      {/* teacher desk, 3D box */}
      <g className="desk">
        <ellipse cx="480" cy="578" rx="262" ry="12" className="fill-foreground/10" />
        <rect x="250" y="466" width="12" height="112" className="fill-foreground/28" />
        <rect x="698" y="466" width="12" height="112" className="fill-foreground/28" />
        <rect x="262" y="466" width="436" height="86" className="fill-foreground/16" />
        <path d="M244 454 L724 454 L724 466 L244 466 Z" className="fill-foreground/32" />
        <path d="M280 410 L704 410 L724 454 L244 454 Z" className="fill-foreground/12" />
      </g>

      {/* teacher monitor: results dashboard, faces the teacher */}
      <g className="teacher-monitor">
        <rect x="550" y="420" width="20" height="12" className="fill-foreground/50" />
        <path d="M530 436 L590 436 L598 428 L538 428 Z" className="fill-foreground/45" />
        <path d="M420 312 L430 304 L690 304 L680 312 Z" className="fill-foreground/55" />
        <path d="M680 312 L690 304 L690 412 L680 420 Z" className="fill-foreground" />
        <g className="screen">
          <rect x="420" y="312" width="260" height="108" rx="9" className="fill-foreground/85" />
          <rect x="428" y="320" width="244" height="92" rx="4" className="fill-card" />
          <rect x="436" y="326" width="228" height="10" rx="3" className="fill-accent/15" />
          <circle cx="443" cy="331" r="3" className="fill-accent" />
          <rect x="451" y="329.5" width="44" height="3" rx="1.5" className="fill-foreground/25" />
          <g className="badge">
            <circle cx="470" cy="368" r="19" className="fill-accent-glow" />
            <path
              d="M462 368 L468 375 L479 361"
              fill="none"
              strokeWidth="4.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="stroke-card"
            />
          </g>
          <rect x="448" y="393" width="44" height="4" rx="2" className="fill-foreground/20" />
          <rect x="448" y="400" width="30" height="4" rx="2" className="fill-foreground/12" />
          {[26, 40, 32, 50, 44].map((h, i) => (
            <rect
              key={i}
              x={514 + i * 30}
              y={404 - h}
              width="18"
              height={h}
              rx="3"
              className={
                [
                  "fill-accent/35",
                  "fill-accent/55",
                  "fill-accent/45",
                  "fill-accent/80",
                  "fill-accent",
                ][i]
              }
            />
          ))}
        </g>
        {/* keyboard */}
        <path d="M440 438 L564 438 L574 450 L430 450 Z" className="fill-foreground/40" />
      </g>

      {/* teacher, seen from behind */}
      <g className="teacher">
        <ellipse cx="330" cy="652" rx="84" ry="10" className="fill-foreground/14" />
        <ellipse cx="330" cy="648" rx="54" ry="7" className="fill-foreground/35" />
        <rect x="321" y="618" width="18" height="30" className="fill-foreground/40" />
        <rect x="246" y="490" width="168" height="130" rx="28" className="fill-foreground/25" />
        <path
          d="M262 616 L262 536 Q262 492 330 488 Q398 492 398 536 L398 604 Q398 618 384 618 L276 618 Q262 618 262 604 Z"
          className="fill-accent"
        />
        <path d={ARM} className="fill-accent" />
        <path d={ARM} className="fill-foreground/10" />
        <circle cx="494" cy="456" r="9" fill="#C68642" />
        <rect x="319" y="476" width="22" height="22" fill="#C68642" />
        <circle cx="330" cy="452" r="34" fill="#C68642" />
        <circle cx="297" cy="456" r="6" fill="#C68642" />
        <circle cx="363" cy="456" r="6" fill="#C68642" />
        <ellipse cx="330" cy="448" rx="36" ry="33" className="fill-foreground" />
        <circle cx="330" cy="416" r="14" className="fill-foreground" />
      </g>

      {/* floating UI */}
      <g transform="translate(66 48)">
        <g className="float-card badge">
          <circle r="36" className="fill-accent-glow/20" />
          <circle r="27" className="fill-accent-glow" />
          <path
            d="M-12 1 L-4 10 L13 -9"
            fill="none"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="stroke-card"
          />
        </g>
      </g>

      <g transform="translate(150 10)">
        <g className="float-card">
          <rect x="0" y="5" width="128" height="58" rx="14" className="fill-foreground/10" />
          <rect width="128" height="58" rx="14" className="fill-card" />
          <Stars />
          <rect x="14" y="42" width="60" height="6" rx="3" className="fill-foreground/15" />
        </g>
      </g>

      <g transform="translate(402 48)">
        <g className="float-card">
          <circle cx="3" cy="5" r="30" className="fill-foreground/10" />
          <circle r="30" className="fill-card" />
          <circle r="19" fill="none" strokeWidth="5" className="stroke-accent/25" />
          <circle
            r="19"
            fill="none"
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray="84 120"
            transform="rotate(-90)"
            className="stroke-accent"
          />
          <path
            d="M0 0 L0 -9"
            fill="none"
            strokeWidth="3"
            strokeLinecap="round"
            className="stroke-foreground"
          />
          <rect x="-5" y="-38" width="10" height="5" rx="2" className="fill-accent" />
        </g>
      </g>

      <g transform="translate(572 4)">
        <g className="float-card">
          <rect x="0" y="5" width="140" height="78" rx="14" className="fill-foreground/10" />
          <rect width="140" height="78" rx="14" className="fill-card" />
          <rect x="14" y="12" width="50" height="6" rx="3" className="fill-foreground/20" />
          {[16, 26, 22, 38, 32].map((h, i) => (
            <rect
              key={i}
              x={18 + i * 24}
              y={68 - h}
              width="14"
              height={h}
              rx="3"
              className={
                [
                  "fill-accent/30",
                  "fill-accent/50",
                  "fill-accent/40",
                  "fill-accent/80",
                  "fill-accent-glow",
                ][i]
              }
            />
          ))}
        </g>
      </g>
    </svg>
  );
}

export default HeroIllustration;
